/**
 * The reports' pure half: periods, the calendar, and the words for each number. Pure so it can be
 * tested without a browser (Pages.render.test.mjs).
 *
 * Days are written YYYY-MM-DD and read as the carrier's days (backend reports.mjs counts them in the
 * carrier's time zone), so "today" is the same day for everyone at the company.
 */

const DAY = 86400000;
export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** A day number (days since 1970) ↔ "YYYY-MM-DD". */
export const dayOf = (ymd) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || "")); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) / DAY : null; };
export const ymdOf = (n) => new Date(n * DAY).toISOString().slice(0, 10);
/** Today, as a day number, in a time zone (the carrier's). */
export function todayIn(tz, now = new Date()) {
  try {
    const p = new Intl.DateTimeFormat("en-CA", { timeZone: tz || "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
    return dayOf(p);
  } catch { return dayOf(now.toISOString().slice(0, 10)); }
}

export const QUICK = [
  { key: "today", label: "Today", days: 1 },
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
];
/** The quick pick these dates are, if any. */
export const quickOf = (start, end, today) => QUICK.find((q) => end === today && start === today - q.days + 1) || null;

const date = (n, withYear) => { const d = new Date(n * DAY); return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}${withYear ? `, ${d.getUTCFullYear()}` : ""}`; };
export const shortDate = (n) => { const d = new Date(n * DAY); return `${MONTHS[d.getUTCMonth()].slice(0, 3)} ${d.getUTCDate()}, ${d.getUTCFullYear()}`; };
const dayName = (n) => WEEKDAYS[new Date(n * DAY).getUTCDay()];
export const clockOf = (hhmm) => { const [h, m] = String(hhmm).split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };

/** "Showing today, Friday, October 30, from 9:00 AM to 1:00 PM." */
export function showingLine({ start, end, today, between, from, to }) {
  const sameYear = new Date(start * DAY).getUTCFullYear() === new Date(today * DAY).getUTCFullYear() && new Date(end * DAY).getUTCFullYear() === new Date(today * DAY).getUTCFullYear();
  let what;
  if (start === today && end === today) what = `today, ${dayName(today)}, ${date(today, false)}`;
  else if (start === end) what = `${dayName(start)}, ${date(start, !sameYear)}`;
  else what = `${date(start, !sameYear)} to ${date(end, !sameYear)}`;
  const hours = between ? `, from ${clockOf(from)} to ${clockOf(to)}${start !== end ? " each day" : ""}` : (start === end ? ", all day" : "");
  return `Showing ${what}${hours}.`;
}

/** One month of the calendar: blanks to the first weekday, then each day with its state. */
export function monthDays(year, month, { start, end, today }) {
  const first = Date.UTC(year, month, 1) / DAY;
  const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const lead = new Date(first * DAY).getUTCDay();
  const out = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= count; d++) {
    const n = first + d - 1;
    out.push({ n, label: String(d), later: n > today, edge: n === start || n === end, inside: n >= start && n <= end, isToday: n === today });
  }
  while (out.length % 7) out.push(null);
  return out;
}

/** "+38 vs the 7 days before", "Same as the day before". */
export function changeWords(cur, prev, { days, unit = "" } = {}) {
  const span = days === 1 ? "the day before" : `the ${days} days before`;
  if (prev == null || cur == null) return null;
  const d = Math.round((cur - prev) * 10) / 10;
  if (d === 0) return `Same as ${span}`;
  const size = Math.abs(d);
  return `${d > 0 ? "+" : "−"}${size}${unit === "points" ? ` ${size === 1 ? "point" : "points"}` : ""} vs ${span}`;
}

export const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : null);
export const money = (cents) => (cents == null ? "" : `$${Math.round(cents / 100).toLocaleString("en-US")}`);
export const count = (n) => (n == null ? "" : Number(n).toLocaleString("en-US"));
